import React, { useEffect } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { IonApp, IonRouterOutlet, IonSplitPane, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

import Menu from './components/Menu';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './theme/minimalist.css';
import './theme/qrscanner.css';
import './theme/tabs.css';
import './theme/modern-ui.css';
import './theme/menu.css';
import './theme/new-tab-bar.css';
import './theme/icon-fix.css';
import MainTabs from './pages/MainTabs';
import { connect } from './data/connect';
import { AppContextProvider } from './data/AppContext';
import { loadConfData, setFavorites } from './data/sessions/sessions.actions';
import { setIsLoggedIn, setUsername, loadUserData } from './data/user/user.actions';
import Account from './pages/Account';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Support from './pages/Support';
import Tutorial from './pages/Tutorial';
import HomeOrTutorial from './components/HomeOrTutorial';
import { Schedule } from "./models/Schedule";
import RedirectToLogin from './components/RedirectToLogin';
import ConnectionStatus from './components/ConnectionStatus';
import EventsModal from './components/EventsModal';
import TestFetch from './components/TestFetch';
import DirectFetch from './components/DirectFetch';
import { initializeErrorSuppression } from './utils/errorSuppression';
import { ionicConfig, initializeAssetPaths, configureIonicIcons } from './config/ionic.config';
import { initializeNavigationFix } from './utils/ionicNavigationFix';
import { debugStorageState } from './utils/dataDebug';
import { appDataService } from './services/AppDataService';
import FavoritesService from './services/FavoritesService';
import { versionCheckService } from './services/VersionCheckService';
import VersionUpdateNotification from './components/VersionUpdateNotification';
import VersionTest from './pages/VersionTest';
import ErrorBoundary from './components/ErrorBoundary';
import CarnetDigital from './pages/CarnetDigital';
import AdministrativeSectionPage from './pages/administrative/AdministrativeSectionPage';
import { startDataService } from './services/StartDataService';
import { isFeatureEnabled } from './config/app.config';

// Inicializar configuraciones de assets antes de setupIonicReact
initializeAssetPaths();
configureIonicIcons();

setupIonicReact(ionicConfig);

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AppContextProvider>
        <IonicAppConnected />
      </AppContextProvider>
    </ErrorBoundary>
  );
};

interface StateProps {
  darkMode: boolean;
  schedule: Schedule;
  eventcode?: string;
}

interface DispatchProps {
  loadConfData: typeof loadConfData;
  loadUserData: typeof loadUserData;
  setIsLoggedIn: typeof setIsLoggedIn;
  setUsername: typeof setUsername;
  setFavorites: typeof setFavorites;
}

interface IonicAppProps extends StateProps, DispatchProps { }

const IonicApp: React.FC<IonicAppProps> = ({ darkMode, eventcode, setIsLoggedIn, setUsername, setFavorites: syncFavorites, loadConfData, loadUserData }) => {

  useEffect(() => {
    const favoritesService = FavoritesService.getInstance();
    const handleFavoritesUpdate = (favorites: string[]) => {
      syncFavorites(favorites);
    };
    favoritesService.addListener(handleFavoritesUpdate);

    // Inicializar supresión de errores conocidos
    initializeErrorSuppression();

    // Inicializar fix de navegación de Ionic (solución oficial para classList error)
    initializeNavigationFix();

    versionCheckService.configure({
      checkIntervalMinutes: 15,
      showNotifications: true,
      autoUpdate: false
    });
    versionCheckService.setupFocusCheck();

    // Función para inicializar la aplicación
    const initializeApp = async () => {
      try {
        console.log('🚀 App: Inicializando aplicación...');

        const startData = await startDataService.loadLatestStartData();
        if (startData) {
          console.log('📦 Start data cargado:', startData.version || 'sin-version');
        }

        // Usar el nuevo AppDataService para cargar todo el estado
        const appState = await appDataService.loadAppState();

        // Actualizar el estado Redux con los datos cargados
        if (appState.hasConferenceData && appState.schedule) {
          console.log('📊 Actualizando Redux con datos de conferencia...');
          await loadConfData();
        }

        // Cargar datos de usuario en Redux
        await loadUserData();

        // Inicializar FavoritesService si el usuario ya está logueado
        if (appState.isLoggedIn && appState.userid) {
          console.log('🔄 App: Usuario logueado detectado, inicializando FavoritesService...');
          try {
            await favoritesService.initialize(appState.userid);
            console.log('✅ App: FavoritesService inicializado correctamente');
          } catch (error) {
            console.warn('⚠️ App: Error al inicializar FavoritesService:', error);
          }
        }

        console.log('🎉 App: Inicialización completada', {
          isLoggedIn: appState.isLoggedIn,
          hasConferenceData: appState.hasConferenceData,
          eventCode: appState.eventcode
        });

        // Debug en desarrollo
        if (process.env.NODE_ENV === 'development') {
          setTimeout(() => {
            debugStorageState();

            // Importar funciones de prueba
            import('./test/appDataServiceTest').then((testModule) => {
              console.log('🧪 Funciones de prueba de AppDataService cargadas');
              console.log('Ejecuta runAllTests() para probar el sistema completo');
            }).catch((error) => {
              console.log('ℹ️ Funciones de prueba no disponibles:', error.message);
            });
          }, 2000);
        }
      } catch (error) {
        console.error('❌ App: Error durante la inicialización:', error);
      }
    };

    initializeApp();

    // Detectar dispositivos iOS con Dynamic Island y aplicar clase CSS específica
    const detectDynamicIsland = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const pixelRatio = window.devicePixelRatio;

      // Detectar iPhone 14 Pro y Pro Max con Dynamic Island usando viewport
      const isDynamicIslandDevice = isIOS && pixelRatio === 3 && (
        (viewportWidth >= 390 && viewportHeight >= 840) || // iPhone 14 Pro / 15 Pro (aproximado)
        (viewportWidth >= 425 && viewportHeight >= 920)    // iPhone 14 Pro Max / 15 Pro Max (aproximado)
      );

      if (isDynamicIslandDevice) {
        document.body.classList.add('dynamic-island-device');
        console.log('Dynamic Island device detected, applying specific styles');
      }

      // También detectar usando safe-area-inset-bottom si está disponible
      const testElement = document.createElement('div');
      testElement.style.paddingBottom = 'env(safe-area-inset-bottom)';
      document.body.appendChild(testElement);

      const computedStyle = window.getComputedStyle(testElement);
      const safeAreaBottom = computedStyle.paddingBottom;

      document.body.removeChild(testElement);

      // Si safe-area-inset-bottom es mayor a 30px, probablemente es un dispositivo con Dynamic Island
      const safeAreaValue = parseInt(safeAreaBottom) || 0;
      if (safeAreaValue > 30) {
        document.body.classList.add('large-safe-area-device');
        console.log(`Large safe area detected: ${safeAreaValue}px`);
      }
    };

    // Ejecutar detección después de que el DOM esté listo
    setTimeout(detectDynamicIsland, 100);

    // Inicializar el servicio de sincronización
    try {
      // Configurar listeners para eventos de conexión
      window.addEventListener('online', () => {
        console.log('Conexión restablecida, intentando sincronizar datos pendientes');
        // Disparar evento para intentar sincronización manual
        const event = new CustomEvent('attemptManualSync', { detail: { timestamp: Date.now() } });
        window.dispatchEvent(event);
      });

      // Configurar listener para actualizaciones de versión disponibles
      window.addEventListener('versionUpdateAvailable', (event) => {
        const { newVersion, isManual } = event.detail;
        console.log(`📱 Nueva versión disponible: v${newVersion} (${isManual ? 'manual' : 'automática'})`);

        // Mostrar toast o notificación al usuario
        // Aquí podrías integrar con IonToast o tu sistema de notificaciones preferido
        if (!isManual) {
          // Solo mostrar notificación automática si no fue verificación manual
          console.log(`🔔 Notificación: Nueva versión v${newVersion} disponible`);
        }
      });

      // Configurar listener para el evento personalizado de sincronización manual
      window.addEventListener('attemptManualSync', () => {
        console.log('Intentando sincronización manual de datos pendientes');

        // Implementación simple de sincronización manual
        const syncPendingData = async () => {
          try {
            // Importar las funciones necesarias
            const { getPendingAttendanceData, markAttendanceAsSynced } = await import('./data/offlineStorage');

            // Obtener datos pendientes
            const pendingData = await getPendingAttendanceData();

            if (pendingData.length === 0) {
              console.log('No hay datos pendientes para sincronizar');
              return;
            }

            console.log(`Sincronizando ${pendingData.length} registros pendientes`);

            // Procesar cada registro pendiente
            const successfulSyncs = [];

            for (const data of pendingData) {
              try {
                // Crear los datos para la solicitud POST
                const formData = new FormData();
                formData.append('qr', data.qr);
                formData.append('userid', data.userid);
                formData.append('userced', data.userced);

                // Realizar la solicitud POST
                const response = await fetch('https://esimposio.com/asistencia.php', {
                  method: 'POST',
                  body: formData,
                  headers: {
                    'Accept': 'application/json',
                  },
                });

                if (response.ok) {
                  const responseData = await response.json();
                  if (responseData.success) {
                    if (data.id !== undefined) {
                      successfulSyncs.push(data.id);
                    }
                  }
                }
              } catch (error) {
                console.error(`Error al sincronizar registro ${data.id}:`, error);
              }
            }

            // Marcar registros sincronizados correctamente
            if (successfulSyncs.length > 0) {
              await markAttendanceAsSynced(successfulSyncs);
              console.log(`${successfulSyncs.length} registros sincronizados correctamente`);
            }
          } catch (error) {
            console.error('Error durante la sincronización manual:', error);
          }
        };

        // Ejecutar la sincronización
        syncPendingData();
      });
    } catch (error) {
      console.error('Error setting up sync listeners:', error);
    }

    return () => {
      favoritesService.removeListener(handleFavoritesUpdate);
      versionCheckService.stopAutoCheck();
      versionCheckService.teardownFocusCheck();
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!eventcode || eventcode === 'nada') {
      versionCheckService.stopAutoCheck();
      return;
    }

    console.log('🔄 App: Iniciando verificación de agenda para el evento:', eventcode);
    void versionCheckService.startAutoCheck(eventcode).then(() => {
      console.log('✅ App: Verificación periódica de agenda activa para:', eventcode);
    }).catch(error => {
      console.warn('⚠️ App: No se pudo iniciar la verificación de agenda:', error);
    });
  }, [eventcode]);

  // Siempre mostrar la aplicación, incluso si no hay datos
  return (
    <ErrorBoundary>
        <IonApp className={`${darkMode ? 'dark-theme' : ''} gradient-bg`}>
            <ConnectionStatus showBadge={true} showToast={true} />
            <EventsModal />
          <IonReactRouter>
            <IonSplitPane contentId="main">
              <Menu />
              <IonRouterOutlet id="main">
                {/*
                We use IonRoute here to keep the tabs state intact,
                which makes transitions between tabs and non tab pages smooth
                */}
                <Route path="/tabs" render={() => <MainTabs />} />
                <Route path="/account" component={Account} />
                <Route
                  exact
                  path="/carnet-digital"
                  render={routeProps => isFeatureEnabled('carnetDigital')
                    ? <CarnetDigital {...routeProps} />
                    : <Redirect to="/tabs" />}
                />
                <Route path="/administrative/:section" component={AdministrativeSectionPage} />
                <Route path="/login" component={Login} />
                <Route path="/signup" component={Signup} />
                <Route path="/support" component={Support} />
                <Route path="/tutorial" component={Tutorial} />
                <Route path="/test-fetch" component={TestFetch} />
                <Route path="/direct-fetch" component={DirectFetch} />
                <Route path="/version-test" component={VersionTest} />
                <Route path="/logout" render={() => {
                  return <RedirectToLogin
                    setIsLoggedIn={setIsLoggedIn}
                    setUsername={setUsername}
                  />;
                }} />
                <Route path="/" component={HomeOrTutorial} exact />
              </IonRouterOutlet>
            </IonSplitPane>
          </IonReactRouter>
        </IonApp>

        {/* Componente de notificación de actualización de versión */}
        <VersionUpdateNotification
          autoShow={true}
          position="top"
          duration={0}
        />
    </ErrorBoundary>
  );
}

export default App;

const IonicAppConnected = connect<{}, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    darkMode: state.user.darkMode,
    schedule: state.data.schedule,
    eventcode: state.user.eventcode
  }),
  mapDispatchToProps: { loadConfData, loadUserData, setIsLoggedIn, setUsername, setFavorites },
  component: IonicApp
});
